import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import "./FleetCoreProductNavigation.css";

const FLEETCORE_PRODUCT_NAVIGATION_ITEMS = [
  {
    id: "overview",
    label: "Overview",
    href: "#fleetcore-overview",
    targetSelector: "#fleetcore-overview",
  },
  {
    id: "accountability",
    label: "Accountability",
    href: "#fleetcore-accountability-gap",
    targetSelector: "#fleetcore-accountability-gap",
  },
  {
    id: "evidence",
    label: "Evidence",
    href: "#fleetcore-drivers",
    targetSelector: "#fleetcore-drivers",
  },
  {
    id: "intelligence",
    label: "Intelligence",
    href: "#fleetcore-fuel-intelligence",
    targetSelector: "#fleetcore-fuel-intelligence",
  },
  {
    id: "operations",
    label: "Operations",
    href: "#fleetcore-real-operations",
    targetSelector: "#fleetcore-real-operations",
  },
  {
    id: "implementation",
    label: "Implementation",
    href: "#fleetcore-implementation",
    targetSelector: "#fleetcore-implementation",
  },
];

const FLEETCORE_PAGE_TOP_TARGET = {
  href: "#fleetcore-page-top",
  targetSelector: "#fleetcore-page-top",
};

const FLEETCORE_DEMONSTRATION_TARGET = {
  href: "#fleetcore-implementation",
  targetSelector:
    ".fleetcore-implementation-responsible-intelligence__conversion",
};

const FLEETCORE_NAVIGATION_GAP = 12;
const FLEETCORE_MANUAL_SELECTION_DURATION = 1400;

function FleetCoreProductNavigationBrandIcon() {
  return (
    <svg
      className="fleetcore-product-navigation__brand-icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M5.25 15.25L6.55 10.85C6.78 10.08 7.49 9.55 8.29 9.55H15.71C16.51 9.55 17.22 10.08 17.45 10.85L18.75 15.25"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M4.5 15.25H19.5V17.15C19.5 17.76 19.01 18.25 18.4 18.25H5.6C4.99 18.25 4.5 17.76 4.5 17.15V15.25Z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />

      <path
        d="M7.25 18.25V19.25M16.75 18.25V19.25"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />

      <path
        d="M8.1 13H9.35M14.65 13H15.9"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />

      <path
        d="M12 4.5V7.25M9.75 5.25L12 3.75L14.25 5.25"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FleetCoreProductNavigationArrowIcon() {
  return (
    <svg
      className="fleetcore-product-navigation__cta-arrow"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.5 9H15M10.5 4.5L15 9L10.5 13.5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function FleetCoreProductNavigation() {
  const navigationRef = useRef(null);
  const linksContainerRef = useRef(null);
  const manualNavigationRef = useRef(null);

  const [activeNavigationItem, setActiveNavigationItem] =
    useState("overview");

  /*
   * Detects whether the main AndUs Navbar is fixed or sticky and places
   * this product navigation directly beneath it.
   */
  useEffect(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return undefined;
    }

    const getExternalPinnedNavigationElements = () =>
      Array.from(
        document.querySelectorAll("header, nav")
      ).filter((element) => {
        if (
          element === navigationElement ||
          navigationElement.contains(element)
        ) {
          return false;
        }

        const styles = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        const isPinned =
          styles.position === "fixed" ||
          styles.position === "sticky";

        const isVisible =
          styles.display !== "none" &&
          styles.visibility !== "hidden" &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 8;

        return isPinned && isVisible;
      });

    const updateStickyPosition = () => {
      const pinnedElements =
        getExternalPinnedNavigationElements();

      const pinnedNavigationBottom = pinnedElements.reduce(
        (currentBottom, element) => {
          const rect = element.getBoundingClientRect();

          return Math.max(currentBottom, rect.bottom);
        },
        0
      );

      navigationElement.style.setProperty(
        "--fleetcore-product-navigation-sticky-top",
        `${Math.max(
          0,
          Math.round(pinnedNavigationBottom)
        )}px`
      );
    };

    updateStickyPosition();

    window.addEventListener("resize", updateStickyPosition);

    let navigationResizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      navigationResizeObserver = new ResizeObserver(
        updateStickyPosition
      );

      getExternalPinnedNavigationElements().forEach(
        (element) => {
          navigationResizeObserver.observe(element);
        }
      );
    }

    return () => {
      window.removeEventListener(
        "resize",
        updateStickyPosition
      );

      navigationResizeObserver?.disconnect();
    };
  }, []);

  const getNavigationOffset = useCallback(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return 0;
    }

    const navigationStyles =
      window.getComputedStyle(navigationElement);

    const navigationTop = Number.parseFloat(
      navigationStyles.top
    );

    const topOffset = Number.isFinite(navigationTop)
      ? Math.max(0, navigationTop)
      : Math.max(
          0,
          navigationElement.getBoundingClientRect().top
        );

    return (
      topOffset +
      navigationElement.offsetHeight +
      FLEETCORE_NAVIGATION_GAP
    );
  }, []);

  /*
   * Tracks section positions rather than only visible intersections.
   * This keeps the correct high-level navigation stage active while
   * the user scrolls through related subsections.
   */
  useEffect(() => {
    let animationFrameId = null;

    const updateActiveNavigationItem = () => {
      animationFrameId = null;

      const navigationOffset = getNavigationOffset();

      const activationPosition =
        window.scrollY + navigationOffset + 4;

      const availableSections =
        FLEETCORE_PRODUCT_NAVIGATION_ITEMS.map(
          (navigationItem) => {
            const sectionElement = document.querySelector(
              navigationItem.targetSelector
            );

            if (!sectionElement) {
              return null;
            }

            return {
              ...navigationItem,
              element: sectionElement,
              top:
                sectionElement.getBoundingClientRect().top +
                window.scrollY,
            };
          }
        ).filter(Boolean);

      if (availableSections.length === 0) {
        return;
      }

      const manualNavigation = manualNavigationRef.current;

      if (
        manualNavigation &&
        performance.now() < manualNavigation.expiresAt
      ) {
        const selectedSection = availableSections.find(
          (section) => section.id === manualNavigation.id
        );

        if (selectedSection) {
          const selectedSectionViewportTop =
            selectedSection.element.getBoundingClientRect().top;

          const reachedSelectedSection =
            Math.abs(
              selectedSectionViewportTop - navigationOffset
            ) <= 24 ||
            (manualNavigation.id === "overview" &&
              window.scrollY <= 2);

          if (!reachedSelectedSection) {
            setActiveNavigationItem((currentItem) =>
              currentItem === manualNavigation.id
                ? currentItem
                : manualNavigation.id
            );

            return;
          }
        }

        manualNavigationRef.current = null;
      } else if (manualNavigation) {
        manualNavigationRef.current = null;
      }

      let nextActiveItem = availableSections[0].id;

      availableSections.forEach((section) => {
        if (section.top <= activationPosition) {
          nextActiveItem = section.id;
        }
      });

      setActiveNavigationItem((currentItem) =>
        currentItem === nextActiveItem
          ? currentItem
          : nextActiveItem
      );
    };

    const requestNavigationUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        updateActiveNavigationItem
      );
    };

    updateActiveNavigationItem();

    window.addEventListener(
      "scroll",
      requestNavigationUpdate,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      requestNavigationUpdate
    );

    return () => {
      window.removeEventListener(
        "scroll",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "resize",
        requestNavigationUpdate
      );

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [getNavigationOffset]);

  /*
   * Keeps the active link visible inside the horizontal navigation on
   * smaller screens.
   */
  useEffect(() => {
    const linksContainer = linksContainerRef.current;

    if (
      !linksContainer ||
      linksContainer.scrollWidth <=
        linksContainer.clientWidth
    ) {
      return;
    }

    const activeLink = linksContainer.querySelector(
      `[data-fleetcore-navigation-id="${activeNavigationItem}"]`
    );

    if (!activeLink) {
      return;
    }

    const desiredScrollPosition =
      activeLink.offsetLeft -
      linksContainer.clientWidth / 2 +
      activeLink.offsetWidth / 2;

    linksContainer.scrollTo({
      left: Math.max(0, desiredScrollPosition),
      behavior: "smooth",
    });
  }, [activeNavigationItem]);

  const scrollToFleetCoreSection = (
    event,
    destination,
    navigationItemId = null
  ) => {
    event.preventDefault();

    const targetElement = document.querySelector(
      destination.targetSelector
    );

    if (!targetElement) {
      return;
    }

    if (navigationItemId) {
      manualNavigationRef.current = {
        id: navigationItemId,
        expiresAt:
          performance.now() +
          FLEETCORE_MANUAL_SELECTION_DURATION,
      };

      setActiveNavigationItem(navigationItemId);
    } else {
      manualNavigationRef.current = null;
    }

    const navigationOffset = getNavigationOffset();

    const targetPosition =
      targetElement.getBoundingClientRect().top +
      window.scrollY -
      navigationOffset;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });

    window.history.replaceState(
      null,
      "",
      destination.href
    );
  };

  return (
    <nav
      ref={navigationRef}
      id="fleetcore-product-navigation"
      className="fleetcore-product-navigation"
      aria-label="FleetCore page navigation"
    >
      <div className="fleetcore-product-navigation__inner">
        <a
          className="fleetcore-product-navigation__brand"
          href={FLEETCORE_PAGE_TOP_TARGET.href}
          onClick={(event) =>
            scrollToFleetCoreSection(
              event,
              FLEETCORE_PAGE_TOP_TARGET,
              "overview"
            )
          }
          aria-label="Return to the top of the FleetCore page"
        >
          <span className="fleetcore-product-navigation__brand-icon-frame">
            <FleetCoreProductNavigationBrandIcon />
          </span>

          <span className="fleetcore-product-navigation__brand-name">
            FleetCore
          </span>
        </a>

        <div
          ref={linksContainerRef}
          className="fleetcore-product-navigation__links"
        >
          {FLEETCORE_PRODUCT_NAVIGATION_ITEMS.map(
            (navigationItem) => {
              const isActive =
                activeNavigationItem === navigationItem.id;

              return (
                <a
                  key={navigationItem.id}
                  data-fleetcore-navigation-id={
                    navigationItem.id
                  }
                  className={[
                    "fleetcore-product-navigation__link",
                    isActive
                      ? "fleetcore-product-navigation__link--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  href={navigationItem.href}
                  aria-current={
                    isActive ? "location" : undefined
                  }
                  onClick={(event) =>
                    scrollToFleetCoreSection(
                      event,
                      navigationItem,
                      navigationItem.id
                    )
                  }
                >
                  {navigationItem.label}
                </a>
              );
            }
          )}
        </div>

        <a
          className="fleetcore-product-navigation__demonstration-cta"
          href={FLEETCORE_DEMONSTRATION_TARGET.href}
          onClick={(event) =>
            scrollToFleetCoreSection(
              event,
              FLEETCORE_DEMONSTRATION_TARGET
            )
          }
        >
          <span className="fleetcore-product-navigation__cta-label fleetcore-product-navigation__cta-label--desktop">
            Book a demonstration
          </span>

          <span className="fleetcore-product-navigation__cta-label fleetcore-product-navigation__cta-label--tablet">
            Book a demo
          </span>

          <span className="fleetcore-product-navigation__cta-label fleetcore-product-navigation__cta-label--mobile">
            Demo
          </span>

          <FleetCoreProductNavigationArrowIcon />
        </a>
      </div>
    </nav>
  );
}